import { Box, Button, Card, CardContent, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { mindtimeBackupsApi } from 'src/api/mindtime/backupsApi';
import { BackupSetsListResponse, BackupSetItem } from 'src/types/mindtime/backups';
import GoogleBackUpSetIcon from 'src/mindtime/icons/GoogleBackUpSetIcon';
import MS365BackUpSetIcon from 'src/mindtime/icons/MS365BackUpSetIcon';

const cardSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #EDF2F7',
  borderRadius: '8px',
};

const headerCellSx = {
  fontWeight: 600,
  fontSize: 11,
  color: '#718096',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.3px',
  px: 2.5,
  py: 1.25,
};

const bodyCellSx = {
  fontSize: 13,
  color: '#2D3748',
  px: 2.5,
  py: 1.5,
  borderBottom: '1px solid #EDF2F7',
};

const iconGrey = '#9CA3AF';

/** Delay before calling run-backup API so the user sees the in-row loader first. */
const RUN_BACKUP_DELAY_MS = 12000;

const mapBackupSetStatusToUi = (
  apiStatus?: string
): BackupActivity['status'] => {
  if (!apiStatus) {
    return 'warning';
  }

  const normalized = apiStatus.toUpperCase();

  if (
    normalized === 'BS_STOP_SUCCESS' ||
    normalized === 'SUCCESS' ||
    normalized === 'OK'
  ) {
    return 'success';
  }

  if (
    normalized.includes('WARNING') ||
    normalized.includes('WARN') ||
    normalized.includes('PARTIAL')
  ) {
    return 'warning';
  }

  if (
    normalized.includes('RUNNING') ||
    normalized.includes('IN_PROGRESS') ||
    normalized.includes('IN PROGRESS')
  ) {
    return 'in_progress';
  }

  if (normalized.includes('FAIL') || normalized.includes('ERROR')) {
    return 'failed';
  }

  return 'warning';
};

export interface BackupActivity {
  id: string;
  name: string;
  type: string;
  typeIcon: 'ms365' | 'google'; // Simplified to only two types
  startTime: string; // from lastBackupJobID
  status: 'success' | 'warning' | 'in_progress' | 'failed'; // Restored
  version: string;
  pc: string;
  os: string;
  to: string;
  runOnServer: boolean;
  backupSetId?: string;
  originalData?: BackupSetItem; // Store original API data for drawer
}

interface RecentBackupActivityCardProps {
  data?: BackupActivity[];
  onViewAll?: () => void;
  onItemClick?: (backupSet: BackupSetItem) => void; // Pass full BackupSetItem for drawer
  organizationId?: string;
}

const RecentBackupActivityCard = ({
  data,
  onViewAll,
  onItemClick,
  organizationId,
}: RecentBackupActivityCardProps) => {
  const [backupSets, setBackupSets] = useState<BackupActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningBackupId, setRunningBackupId] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // If data is provided via props, use it (for testing/override)
    if (data && data.length > 0) {
      setBackupSets(data);
      setLoading(false);
      return;
    }

    // Otherwise, fetch from API
    setLoading(true);
    setError(null);
    mindtimeBackupsApi
      .getBackupSetsList(organizationId)
      .then((response: BackupSetsListResponse) => {
        if (response.Status === 'Error') {
          setError(response.Message || 'Failed to load backup sets');
          setLoading(false);
          return;
        }

        if (!response.Data || response.Data.length === 0) {
          setBackupSets([]);
          setLoading(false);
          return;
        }

        // Map API response to component data structure
        const mappedData: BackupActivity[] = response.Data.map((item) => {
          // Determine typeIcon based on type field - only two types
          let typeIcon: 'ms365' | 'google' = 'ms365'; // Default to ms365
          const typeLower = item.type.toLowerCase();

          if (typeLower.includes('office 365')) {
            typeIcon = 'ms365';
          } else if (typeLower.includes('google')) {
            typeIcon = 'google';
          }

          return {
            id: item.backupSetID || item.name, // Use backupSetID if available
            name: item.name,
            type: item.type,
            typeIcon,
            startTime: item.lastBackupJobID, // Format will be applied in render
            status: mapBackupSetStatusToUi(item.status),
            version: item.version,
            pc: item.pc,
            os: item.os,
            to: item.to,
            runOnServer: item.runOnServer,
            backupSetId: item.backupSetID || item.name, // Use backupSetID from API
            originalData: item, // Store original API data for drawer
          };
        });

        setBackupSets(mappedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch backup sets list:', err);
        setError(err.message || 'Failed to load backup sets');
        setLoading(false);
      });
  }, [data, organizationId]);

  const activityData: BackupActivity[] = backupSets;

  // Helper function to format lastBackupJobID string to readable date/time
  const formatStartTime = (lastBackupJobID: string): string => {
    if (!lastBackupJobID || lastBackupJobID === '-') return '-';

    // Format: "2026-02-19-22-00-00" -> "Feb 19, 2026 22:00:00"
    try {
      const parts = lastBackupJobID.split('-');
      if (parts.length >= 6) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        const hour = parts[3];
        const minute = parts[4];
        const second = parts[5];

        const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      }
    } catch (formatError) {
      console.error('Error formatting start time:', formatError);
    }

    return lastBackupJobID; // Return original if parsing fails
  };

  const getSourceIcon = (type: 'ms365' | 'google'): JSX.Element => {
    switch (type) {
      case 'ms365':
        return <MS365BackUpSetIcon />;
      case 'google':
        return <GoogleBackUpSetIcon />;
      default:
        return <MS365BackUpSetIcon />; // Default fallback
    }
  };

  const handleRowClick = (activity: BackupActivity) => {
    if (onItemClick && activity.originalData) {
      onItemClick(activity.originalData);
    }
  };

  const handleRunBackup = async (event: React.MouseEvent, activity: BackupActivity) => {
    event.stopPropagation();

    if (!activity.backupSetId) {
      toast.error('Backup set ID is missing, unable to start backup.');
      return;
    }

    setRunningBackupId(activity.backupSetId);

    try {
      if (!isMountedRef.current) {
        return;
      }

      const runResponse = await mindtimeBackupsApi.runBackupSet(
        activity.backupSetId,
        organizationId
      );

      if (!isMountedRef.current) {
        return;
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, RUN_BACKUP_DELAY_MS);
      });

      if (runResponse?.Status === 'OK') {
        toast.success(`Backup started for "${activity.name}".`);
      } else {
        toast.error(runResponse?.Message || 'Failed to start backup. Please try again.');
      }
    } catch (runError) {
      if (!isMountedRef.current) {
        return;
      }
      const apiErrorMessage =
        (runError as any)?.response?.data?.Message ||
        (runError as Error)?.message ||
        'Failed to start backup. Please try again.';
      toast.error(apiErrorMessage);
    } finally {
      if (isMountedRef.current) {
        setRunningBackupId(null);
      }
    }
  };

  const getStatusBadge = (status: 'success' | 'warning' | 'in_progress' | 'failed') => {
    const isSuccess = status === 'success';
    const isWarning = status === 'warning' || status === 'in_progress';
    const bg = isSuccess ? '#D1FAE5' : isWarning ? '#FEF3C7' : '#FEE2E2';
    const dotColor = isSuccess ? '#16A34A' : isWarning ? '#EA580C' : '#DC2626';
    const text = isSuccess ? 'SUCCESS' : isWarning ? 'WARNING' : 'FAILED';
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 10,
          bgcolor: bg,
          fontSize: 12,
          fontWeight: 600,
          color: dotColor,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor }} />
        {text}
      </Box>
    );
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{ gap: 1 }}
          px={2.5}
          pt={2}
          pb={1.5}
          borderBottom="1px solid #EDF2F7"
        >
          <SyncIcon sx={{ fontSize: 18, color: '#3182CE' }} />
          <Typography variant="h6" style={{ fontWeight: 600, color: '#2D3748' }}>
            Backup Sets
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : activityData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <Typography variant="body2" color="textSecondary">
              No backup sets found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx }}>TYPE</TableCell>
                  <TableCell sx={{ ...headerCellSx }}>NAME</TableCell>
                  <TableCell sx={{ ...headerCellSx }}>LAST BACKUP</TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    STATUS
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    VERSION
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    PC
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    OS
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    TO
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx }} align="center">
                    ACTIONS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityData.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    onClick={() => item.backupSetId && handleRowClick(item)}
                    sx={{
                      cursor: item.backupSetId ? 'pointer' : 'default',
                    }}
                  >
                    <TableCell sx={bodyCellSx}>
                      {getSourceIcon(item.typeIcon)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {formatStartTime(item.startTime)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      {getStatusBadge(item.status)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {item.version}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {item.pc}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {item.os}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#2D3748' }}>
                        {item.to}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      {runningBackupId === item.backupSetId ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ minWidth: 36, minHeight: 36 }}
                        >
                          <CircularProgress size={22} sx={{ color: iconGrey }} />
                        </Box>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(event) => handleRunBackup(event, item)}
                          sx={{
                            color: iconGrey,
                            '&:hover': { bgcolor: 'rgba(156, 163, 175, 0.1)' },
                          }}
                          title="Run backup"
                        >
                          <PlayArrowIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {onViewAll && (
          <Box display="flex" justifyContent="flex-end" px={2.5} py={2}>
            <Button
              onClick={onViewAll}
              sx={{
                color: '#3182CE',
                fontSize: 12,
                fontWeight: 500,
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              VIEW ALL ACTIVITY
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentBackupActivityCard;
